import React from 'react';
import styled from 'styled-components';
import {
  IPriceCalculator,
  ISubscriptionState,
  PaymentPlanType,
  SubscriptionType,
  UserType,
} from '../../constants/types';
import { branch, compose, renderComponent, withState } from 'recompose';
import Loading from '../Loading';
import {
  getSubscriptionIds,
  getSubscriptionsForAction,
} from '../../constants/payment';
import { RouterProps, withRouter } from 'react-router';
import {
  cancelSubscriptionMutation,
  getUserAccessQuery,
} from '../../constants/gql/graphqlDefaultRequests';
import {
  GET_PAGE_TITLE_BY_ROLE_MSG,
  GET_PLAN_TYPES_FOR_USER_ROLE,
  GET_SUITABLE_PLANS,
} from '../../pages/Landing/LnPricing/constants';
import PricingPlan from '../../pages/Landing/LnPricing/Plans/PricingPlan';
import {
  CURRENT_PLAN,
  CANCELED_PLAN,
  PLAN_BLOCKED,
  PENDING_PLAN,
  PLAN_COMMITMENT,
} from '../../pages/Landing/LnPlatform/constants';
import { UserAccess } from 'menhealth-subscription-validation';
import ConfirmationModal from '../Modals/ConfirmationsModal/ConfirmationModal';
import {
  CANCEL_SUBSCRIPTION_MSG,
  CONFIRM_CANCEL_SUBSCRIPTION_MSG,
  STOP_RENEW_SUBSCRIPTION_MSG,
} from '../../constants/localization';
import ChangePaymentMethodModal from '../Modals/ChangePaymentMethodModal/ChangePaymentMethodModal';
import { graphql } from 'react-apollo';
import {
  ACTIVE_USER_SUBSCRIPTIONS,
  PATIENTS_COUNT_FOR_DOCTOR,
  PAYMENT_PLANS_BY_ROLE,
} from '../../constants/gql/payment';
import { OptionProps } from '@apollo/react-hoc/lib/types';
import { SubTitle } from '../../constants/styled';
import CurrentSubscription from '../CurrentSubscription';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: ${(props) => props.theme.fonts.regular};
  margin: 30px 20px 0 20px;
  padding-bottom: 20px;

  @media (max-width: 1350px) {
    margin: 30px 10px 0 10px;
  }

  @media (max-width: 930px) {
    flex-direction: column;
  }
`;

const PlanSubTitle = styled(SubTitle)`
  text-align: -webkit-center;
`;

const Commitment = styled(SubTitle)`
  text-align: center;
  color: dimgrey;
`;

const Plans: React.FunctionComponent<IProps> = ({
  plans,
  user,
  priceCalculator,
  subscriptionState,
  switchAnotherPlan,
  forceEnableRenewal = false,
  switchMessage,
  confirmCancelSubIsOpen,
  toggleConfirmCancelSub,
  toggleChangePaymentMethod,
  changePaymentMethodModalIsOpen,
  activeSubscriptions,
  cancelSubscriptions,
  maxPatients,
}) => {
  return (
    <Wrapper>
      <PlanSubTitle>{GET_PAGE_TITLE_BY_ROLE_MSG(user.role)}</PlanSubTitle>
      {
        <>
          {plans &&
            plans.map((paymentPlanType) => (
              <PricingPlan
                isRenewableSubscription={subscriptionState.isRenewableSubscriptionPlan(
                  paymentPlanType,
                )}
                isIOSSubscription={
                  activeSubscriptions[0] &&
                  activeSubscriptions[0].subscriptionHost === 'IOS'
                }
                isCancelable={subscriptionState.isCancelable(paymentPlanType)}
                isPaymentMethodChangeable={subscriptionState.isPaymentMethodChangeable(
                  paymentPlanType,
                )}
                isActivePlan={subscriptionState.isActivePlan(paymentPlanType)}
                isPendingPlan={subscriptionState.isPendingPlan(paymentPlanType)}
                planId={paymentPlanType.planId}
                key={paymentPlanType.planId}
                price={priceCalculator.getPrice(paymentPlanType)}
                header={priceCalculator.getHeader(paymentPlanType)}
                description={priceCalculator.getDescription(paymentPlanType)}
                isLabeled={
                  subscriptionState.isActivePlan(paymentPlanType) ||
                  subscriptionState.isRenewableSubscriptionPlan(
                    paymentPlanType,
                  ) ||
                  subscriptionState.isBlocked(paymentPlanType)
                }
                label={
                  subscriptionState.isPendingPlan(paymentPlanType)
                    ? PENDING_PLAN
                    : subscriptionState.isActivePlan(paymentPlanType)
                    ? CURRENT_PLAN
                    : subscriptionState.isBlocked(paymentPlanType)
                    ? PLAN_BLOCKED
                    : subscriptionState.isRenewableSubscriptionPlan(
                        paymentPlanType,
                      )
                    ? CANCELED_PLAN
                    : ''
                }
                chooseDisabled={
                  subscriptionState.isDisabled(paymentPlanType) ||
                  subscriptionState.isActivePlan(paymentPlanType) ||
                  subscriptionState.isBlocked(paymentPlanType)
                }
                currency={paymentPlanType.currency}
                role={user.role}
                maxPatients={maxPatients}
                switchAnotherPlan={switchAnotherPlan}
                switchMessage={switchMessage}
                openChangePaymentMethodModal={() =>
                  toggleChangePaymentMethod(true)
                }
                openConfirmCancelSubModal={() => toggleConfirmCancelSub(true)}
                forceEnableRenewal={forceEnableRenewal}
              />
            ))}
          {activeSubscriptions?.length > 0 && (
            <CurrentSubscription
              role={user.role}
              subscription={activeSubscriptions[0]}
              canShowExpiresOn={subscriptionState.isRenewableSubscriptionPlan(
                null,
              )}
              isCanceled={subscriptionState.isRenewableSubscriptionPlan(null) || !user.isTrial}
            />
          )}
          <Commitment>{PLAN_COMMITMENT}</Commitment>
          <ConfirmationModal
            isOpen={confirmCancelSubIsOpen}
            toggleModal={toggleConfirmCancelSub}
            title={STOP_RENEW_SUBSCRIPTION_MSG}
            description={CONFIRM_CANCEL_SUBSCRIPTION_MSG}
            onConfirm={() =>
              cancelSubscriptions(
                getSubscriptionIds(
                  activeSubscriptions.filter((s) => s.status !== 'Canceled'),
                ),
              )
            }
            confirmText={CANCEL_SUBSCRIPTION_MSG}
          />
          <ChangePaymentMethodModal
            isOpen={changePaymentMethodModalIsOpen}
            toggleModal={toggleChangePaymentMethod}
            activeSubscriptions={activeSubscriptions}
          />
        </>
      }
    </Wrapper>
  );
};

export default compose<IPlansPropsInner, IPlansPropsOuter>(
  getUserAccessQuery<IPlansPropsInner>(),
  graphql(ACTIVE_USER_SUBSCRIPTIONS, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data }: OptionProps<IProps>) => {
      const subscriptionsForActions: SubscriptionType[] | null =
        data.activeUserSubscriptions &&
        getSubscriptionsForAction(data.activeUserSubscriptions);
      return {
        activeSubscriptions: subscriptionsForActions,
        loadingActiveSubscriptions: data.loading,
        allValidSubscriptions: data.activeUserSubscriptions,
      };
    },
  }),
  graphql(PAYMENT_PLANS_BY_ROLE, {
    skip: ({ loadingActiveSubscriptions }: IProps) =>
      loadingActiveSubscriptions,
    options: ({ activeSubscriptions, user }: IProps) => ({
      variables: {
        role: user.role,
        planId:
          activeSubscriptions &&
          activeSubscriptions[0] &&
          activeSubscriptions[0].planId,
      },
    }),
    props: ({ ownProps, data }: OptionProps<IProps>) => {
      const code = ownProps.user.promoCode;

      let planId = null;

      const clinicWithPlan = ownProps.user.clinics.find(
        (clinic) => clinic.plan.planId,
      );

      if (clinicWithPlan) {
        planId = clinicWithPlan.plan.planId;
      }

      const doctorWithPlan = ownProps.user.doctors.find(
        (doctor) => doctor.plan.planId,
      );

      if (doctorWithPlan) {
        planId = doctorWithPlan.plan.planId;
      }

      const userPlanId = ownProps.user.plan.planId;

      if (userPlanId) {
        planId = userPlanId;
      }

      return {
        plans: !data.loading
          ? GET_SUITABLE_PLANS(data.plans, code, planId).filter((p) => {
              return GET_PLAN_TYPES_FOR_USER_ROLE(ownProps.user.role).includes(
                p.planType,
              );
            })
          : null,
        loadingPlans: data.loading,
      };
    },
  }),
  branch(
    ({ user }: IProps) => user.role === 'DOCTOR',
    graphql(PATIENTS_COUNT_FOR_DOCTOR, {
      props: ({ data }: OptionProps<IProps>) => ({
        patientCount: data.count,
      }),
    }),
  ),
  branch(
    ({
      loadingPlans,
      loadingUserHasSubscription,
      loadingActiveSubscriptions,
    }) =>
      loadingPlans || loadingUserHasSubscription || loadingActiveSubscriptions,
    renderComponent(Loading),
  ),
  withRouter,
  withState('isConfirmDisabled', 'setIsConfirmDisabled', true),
  withState('confirmCancelSubIsOpen', 'toggleConfirmCancelSub', false),
  withState('isPaymentModalOpen', 'setPaymentModalOpen', false),
  withState('changePlanIsOpen', 'toggleChangePlan', false),
  withState(
    'changePaymentMethodModalIsOpen',
    'toggleChangePaymentMethod',
    false,
  ),
  cancelSubscriptionMutation,
)(Plans);

type IProps = IPlansPropsInner & IPlansPropsOuter;

interface IPlansPropsInner extends RouterProps {
  activeSubscriptions?: SubscriptionType[];
  allValidSubscriptions?: SubscriptionType[];
  loadingActiveSubscriptions: boolean;
  userAccess: UserAccess;
  toggleConfirmCancelSub: (value: boolean) => void;
  confirmCancelSubIsOpen: boolean;
  toggleChangePaymentMethod: (value: boolean) => void;
  changePaymentMethodModalIsOpen: boolean;
  cancelSubscriptions: (ids: string[]) => Promise<void>;
  isConfirmDisabled: boolean;
  setIsConfirmDisabled: (value: boolean) => void;
  planDisabledForChoose: boolean;
  isRenewedSubscription: boolean;
  setIsRenewedSubscription: (value: boolean) => void;
  loadingUserHasSubscription: boolean;
  plans: PaymentPlanType[];
}

interface IPlansPropsOuter {
  user: UserType;
  maxPatients: number;
  priceCalculator: IPriceCalculator;
  switchAnotherPlan?: () => void;
  subscriptionState: ISubscriptionState;
  switchMessage?: string;
  forceEnableRenewal?: boolean;
}
